import { action } from '@ember/object';
import type RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { humanize } from 'ember-cli-string-helpers/helpers/humanize';
import type StoreService from 'ember-data/store';
import type DeskModel from 'garaje/models/desk';
import type MapFeatureModel from 'garaje/models/map-feature';
import type LiveMapService from 'garaje/services/live-map';
import kebabCase from 'lodash/kebabCase';

/**
 * Next desk assignment data from GraphQL search results
 * Defined locally until schema.graphql is updated with this type
 */
interface NextAssignment {
  deskId: string;
  deskName: string;
  startTime: string;
}

/**
 * Employee data from GraphQL search results
 */
interface EmployeeGraphQLData {
  id: number;
  name: string;
  email: string;
  profilePictureUrl?: string;
  nextAssignment?: NextAssignment;
}

/**
 * Transformed employee data for display and selection
 */
interface EmployeeDisplayItem {
  id: string;
  name: string;
  email: string;
  profilePictureUrl?: string;
  nextAssignment?: NextAssignment;
}

interface MapFeatureSearchResult {
  type: string;
  __typename: 'MapFeatureSearchResultItem';
  feature: {
    id: string;
    name: string;
    floor: {
      id: string;
      name: string;
      __typename: 'MapFloor';
    };
    __typename: 'MapPointOfInterestFeature';
  };
}

interface EmployeeSearchResult {
  type: string;
  __typename: 'EmployeeMapSearchResultItem';
  employee: EmployeeGraphQLData;
}

type MapSearchResultItem = MapFeatureSearchResult | EmployeeSearchResult;

interface SearchResultsListArgs {
  searchResults: MapSearchResultItem[];
  closeDropdown?: () => void;
  mapFeatures: MapFeatureModel[];
  setSearchInput?: (value: string) => void;
  setMapSearchNoResultsText?: (value: string) => void;
  mapType: 'live' | 'edit' | 'draft';
  onEmployeeSelect?: (nextAssignment: NextAssignment | undefined) => void;
}

export default class SearchResultsList extends Component<SearchResultsListArgs> {
  // Resource types that should be categorized as "Resources"
  private readonly RESOURCE_TYPES = ['PARKING', 'DESK', 'ROOM'];

  @service declare router: RouterService;
  @service declare store: StoreService;
  @service declare liveMap: LiveMapService;

  get resources(): Array<{ id: string; name: string; description: string; type: string }> {
    return this.args.searchResults
      .filter(
        (item): item is MapFeatureSearchResult =>
          item.__typename === 'MapFeatureSearchResultItem' &&
          item.feature?.name !== undefined &&
          this.RESOURCE_TYPES.includes(item.type),
      )
      .map((item) => ({
        id: item.feature.id,
        name: item.feature.name,
        description: `${humanize([item.type])} • ${item.feature.floor.name}`,
        iconClassName: item.type.toLowerCase(),
        floorId: item.feature.floor.id,
        type: 'resource',
      }));
  }

  get pointsOfInterest(): Array<{ id: string; name: string; description: string; type: string }> {
    return this.args.searchResults
      .filter(
        (item): item is MapFeatureSearchResult =>
          item.__typename === 'MapFeatureSearchResultItem' &&
          item.feature?.name !== undefined &&
          !this.RESOURCE_TYPES.includes(item.type),
      )
      .map((item) => ({
        id: item.feature.id,
        name: item.feature.name,
        description: `${humanize([item.type])} • ${item.feature.floor.name}`,
        iconClassName: kebabCase(item.type.toLowerCase()),
        floorId: item.feature.floor.id,
        type: 'point-of-interest',
      }));
  }

  get employees(): Array<EmployeeDisplayItem & { type: string }> {
    return this.args.searchResults
      .filter(
        (item): item is EmployeeSearchResult =>
          item.__typename === 'EmployeeMapSearchResultItem' && item.employee?.name !== undefined,
      )
      .map((item) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-explicit-any
        const nextAssignment = (item.employee as any).nextAssignment as NextAssignment | undefined;
        return {
          id: item.employee.id.toString(),
          name: item.employee.name,
          email: item.employee.email,
          profilePictureUrl: item.employee.profilePictureUrl?.includes('gravatar')
            ? undefined
            : item.employee.profilePictureUrl,
          // nextAssignment is not yet in generated GraphQL schema, will be added when backend deploys
          nextAssignment,
          type: 'employee' as const,
        };
      });
  }

  get hasResults(): boolean {
    return this.resources.length > 0 || this.pointsOfInterest.length > 0 || this.employees.length > 0;
  }

  handleNoSearchResults(employeeName: string): void {
    if (this.args.setMapSearchNoResultsText) {
      this.args.setMapSearchNoResultsText(`${employeeName} does not have an assignment in this location`);
    }
  }

  handleSelectEmployeeError(employeeName: string): void {
    this.handleNoSearchResults(employeeName);
    if (this.args.setSearchInput) {
      this.args.setSearchInput(employeeName);
    }
    if (this.args.closeDropdown) {
      this.args.closeDropdown();
    }
  }

  async handleTransition(selectedMapFeatureId: string | undefined, floorId: string): Promise<void> {
    switch (this.args.mapType) {
      case 'edit':
        await this.router.transitionTo('spaces.maps.edit.show', {
          queryParams: {
            floor_id: floorId,
            ...(selectedMapFeatureId ? { selectedFeatureId: selectedMapFeatureId } : {}),
          },
        });
        break;
      case 'live':
        await this.router.transitionTo('spaces.maps.live.show', floorId, {
          queryParams: {
            ...(selectedMapFeatureId ? { selectedFeatureId: selectedMapFeatureId } : {}),
          },
        });
        break;
      case 'draft': {
        // Get the area map ID from the parent route parameters using router.recognize
        const areaMapId = this.router.currentRoute.parent?.params?.['area_map_id'];
        if (areaMapId) {
          await this.router.transitionTo('spaces.maps.drafts.area-map.show', areaMapId, {
            queryParams: {
              floor_id: floorId,
              ...(selectedMapFeatureId ? { selectedFeatureId: selectedMapFeatureId } : {}),
            },
          });
        }
        break;
      }
    }
  }

  @action
  async handleSelectMapFeature(result: { id: string; floorId: string; name: string }): Promise<void> {
    await this.handleTransition(result.id, result.floorId);

    if (this.args.closeDropdown) {
      this.args.closeDropdown();
    }
    if (this.args.setSearchInput) {
      this.args.setSearchInput(result.name);
    }
  }

  @action
  async handleSelectEmployee(selectedEmployee: EmployeeDisplayItem): Promise<void> {
    const { mapFeatures, closeDropdown, setSearchInput, onEmployeeSelect } = this.args;

    // Use nextAssignment if available from GraphQL
    if (selectedEmployee.nextAssignment) {
      const { deskId } = selectedEmployee.nextAssignment;

      // Only proceed with GraphQL path if deskId is valid
      if (deskId) {
        // Get floorId from the desk record — works even when employee is on a different floor
        const desk = this.store.peekRecord('desk', deskId);
        const floorId = desk?.belongsTo('floor').id();

        if (floorId) {
          // Find the map feature — may be undefined if the desk is on a different floor
          // than the one currently loaded in mapFeatures
          const selectedMapFeature = mapFeatures.find((mapFeature) => mapFeature.externalId === deskId);

          // Notify controller about the selected nextAssignment for popup display
          if (onEmployeeSelect) {
            onEmployeeSelect(selectedEmployee.nextAssignment);
          }

          // Transition to the correct floor; selectedMapFeature may be undefined for
          // cross-floor searches (desk highlight will not appear but floor redirect will)
          await this.handleTransition(selectedMapFeature?.id, floorId);

          if (closeDropdown) {
            closeDropdown();
          }
          if (setSearchInput) {
            setSearchInput(selectedEmployee.name);
          }
          return;
        }
      }
    }

    // Legacy fallback: search in local store
    let allDesks = this.store.peekAll('desk').slice();

    // In draft map, only show desks that are part of the draft
    if (this.args.mapType === 'draft') {
      allDesks = allDesks.filter((d: DeskModel) => d.parentDeskId != null);
    }

    if (allDesks.length === 0) {
      this.handleSelectEmployeeError(selectedEmployee.name);
      return;
    }

    const selectedDesk = this.findEmployeeDesk(selectedEmployee, allDesks);

    if (!selectedDesk) {
      this.handleSelectEmployeeError(selectedEmployee.name);
      return;
    }

    const floor = await selectedDesk.floor;

    if (!floor) {
      this.handleSelectEmployeeError(selectedEmployee.name);
      return;
    }

    // May be undefined when employee's desk is on a different floor than currently loaded
    const selectedMapFeature = mapFeatures.find((mapFeature) => mapFeature.externalId === selectedDesk.id);

    // Clear nextAssignment when using legacy path (no GraphQL data)
    if (onEmployeeSelect) {
      onEmployeeSelect(undefined);
    }

    // Transition to the correct floor; selectedMapFeature may be undefined for
    // cross-floor searches (desk highlight will not appear but floor redirect will)
    await this.handleTransition(selectedMapFeature?.id, floor.id);

    if (closeDropdown) {
      closeDropdown();
    }
    if (setSearchInput) {
      setSearchInput(selectedEmployee.name);
    }
  }

  findEmployeeDesk(selectedEmployee: EmployeeDisplayItem, allDesks: DeskModel[]): DeskModel | undefined {
    if (this.args.mapType === 'live') {
      // In live map: try reserved desk first, then fall back to assigned desk
      const reservedDeskState = this.liveMap.getUserReservation(selectedEmployee.id);
      if (reservedDeskState) {
        const reservedDesk = allDesks.find((desk) => desk.id === reservedDeskState.resourceId);
        if (reservedDesk) {
          return reservedDesk;
        }
      }

      // Fall back to assigned desk
      return allDesks.find((desk) => desk.assignedTo === selectedEmployee.email);
    } else {
      // In edit map: only look for assigned desk
      return allDesks.find((desk) => desk.assignedTo === selectedEmployee.email);
    }
  }
}
